<template>
  <div class="block-detail-blog">
    <div class="block-detail-blog__info">
      <p class="block-detail-blog__info-date">
        {{ date }}
      </p>
      <p v-if="readingTime" class="block-detail-blog__info-reading">
        <AppIcon
          icon="clock"
          :is-raw="true"
        />
        {{ readingTime }}
      </p>
      <div
        v-if="author || expert.name"
        class="block-detail-blog__info-author"
      >
        <AtomImage
          v-if="expert.image"
          :src="expert.image"
        />

        <p v-if="expert.name || author">
          {{ useTranslation('general', 'from') }}
        </p>

        <SlotLink
          v-if="expert.name"
          :url="expert.full_slug"
          class="block-detail-blog__info-author-link"
        >
          {{ expert.name }}
        </SlotLink>
        <p v-if="author && !expert.name">
          {{ author }}
        </p>
      </div>
    </div>
    <AtomHeading
      v-if="data.title"
      tag="h1"
      appereance="h1"
      :text="data.title"
      class="block-detail-blog__title"
    />
    <AtomLead
      v-if="data.lead"
      class="block-detail-blog__lead"
      :text="data.lead"
    />
    <BlockSocialShare
      v-if="data.social_share + data.social_share.length > 0"
      class="block-detail-blog__social-share"
      :data="data.social_share"
      :title="data.title"
      :link="link"
    />
  </div>
</template>

<script setup>
defineProps({
    data: {
        type: Object,
        default: () => {},
    },
});

/*
  Settings
*/
const storyStore = useStoryStore();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const { dayjs } = useDayJs();
const storyblokApi = useStoryblokApi();
const expert = ref({});
const getExpert = async (uuid) => {
    if (uuid === '') return;
    try {
        const { data } = await storyblokApi.get('cdn/stories/', {
            starts_with: 'experten/',
            version: 'published',
            by_uuids: uuid.toString(),
        });

        const expertData = data?.stories[0];
        expert.value = expertData;
        const strapiData = JSON.parse(expertData.content.strapi_data);
        expert.value.image = strapiData?.block_author_image?.url;
    } catch (e) {
        useSentryError(e);
    }
};

/*
  Computed values
*/
const currentStory = computed(() => storyStore.getCurrentStory);
const date = computed(() => dayjs(storyStore.getCurrentStoryFirstPublishedDate).format('MMMM YYYY'));

const author = currentStory.value?.story?.content?.author;
const uuidExpert = currentStory.value?.story?.content?.expert || '';
const readingTime = currentStory.value?.story?.content?.reading_time || '';
getExpert(uuidExpert);

const { BASE_URL } = runtimeConfig.public;
const link = computed(() => BASE_URL + route.path);
</script>

<style lang="scss" scoped>
.block-detail-blog {
    @include component-spacing(40px, 122px);
    @include grid-columns();
    @include grid-layout();

    margin-top: 86px;

    @include tablet {
        margin-top: 20px;
    }
}

.block-detail-blog__info {
    @include grid-columns(1, 7);
    @include typo-font('regular');
    @include typo-size('nav');
    display: flex;
    align-items: center;

    color: $C_GREY_DARK;
    column-gap: 20px;
    text-transform: uppercase;

    @include tablet {
        @include default-content-columns();
    }
}

.block-detail-blog__info-reading {
    @include grid-columns(1, 2);
    @include typo-font('regular');
    @include typo-size('nav');

    display: flex;
    align-items: center;
    color: $C_GREEN_DARK;
    column-gap: 8px;
}

.block-detail-blog__info-author {
    @include typo-font('regular');
    @include typo-size('a');

    display: flex;
    align-items: center;
    color: $C_GREEN_DARK;
    column-gap: 8px;
}

.block-detail-blog__info-author-link {
    color: $C_PRIMARY;
    transition: color 0.15s ease-in-out;

    &:hover {
        color: $C_SECONDARY;
    }
}

.block-detail-blog__title {
    @include grid-columns(1, 8);

    @include tablet {
        @include default-content-columns();
    }
}

.block-detail-blog__lead {
    @include grid-columns(10, 9);

    margin-top: 130px;

    @include tablet {
        @include default-content-columns();

        margin-top: 10px;
    }
}
.block-detail-blog__social-share {
    @include grid-columns(10, 9);

    margin-top: 16px;

    @include tablet {
        @include default-content-columns();
    }

    @include mobile {
        margin-top: 20px;
    }
}
</style>
